import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import StatsTable, { TableColumn, TableRow, sharedScrollbarStyles } from '../common/StatsTable';
import { LoadingAnimation } from '../common/LoadingAnimation';

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  
  @media (max-width: 30rem) {
    justify-content: center;
  }
`;

const ScrollWrapper = styled.div`
  position: relative;
  max-height: clamp(320px, 45vh, 540px);
  overflow: hidden;
  border-radius: 12px;
  background: var(--color-surface);
`;

const ScrollInner = styled.div`
  overflow: auto;
  max-height: inherit;
  ${sharedScrollbarStyles};

  .stats-table-scroll-content {
    width: max-content;
  }
`;

const ToggleControl = styled.div`
  display: inline-flex;
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  padding: 0.15rem;
`;

const ToggleOption = styled.button<{ $active?: boolean }>`
  padding: 0.35rem 0.85rem;
  border: none;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $active }) => ($active ? 'var(--color-accent)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#ffffff' : 'var(--color-text-secondary)')};

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--color-accent-light)' : 'var(--color-surface)')};
    color: ${({ $active }) => ($active ? '#ffffff' : 'var(--color-text-primary)')};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

interface InsightsLineupsStatsTableProps {
  statsType: 'by_minutes' | 'by_efficiency';
  lineupStatsData: any[];
  loading?: boolean;
  error?: string | null;
}

export const InsightsLineupsStatsTable: React.FC<InsightsLineupsStatsTableProps> = ({
  statsType: _statsType,
  lineupStatsData,
  loading = false,
  error = null
}) => {
  const [activeView, setActiveView] = useState<'stats_totals' | 'breakdown'>('stats_totals');

  const sanitizeLineupRows = useMemo(() => {
    if (!Array.isArray(lineupStatsData)) return [];

    return lineupStatsData.map((row: any) => {
      const statsTotals = row?.statsTotals || row?.stats_totals || {};
      const breakdown = row?.breakdown || {};

      return {
        lineup: row?.lineup || statsTotals?.LINEUP || row?.metadata?.lineup || '-',
        statsTotals,
        breakdown
      };
    });
  }, [lineupStatsData]);

  const formatColumnLabel = (key: string): string => {
    if (!key) return '';
    return key.replace(/_/g, ' ').replace(/\s{2,}/g, ' ').toUpperCase();
  };

  const { totalsRows, breakdownRows } = useMemo(() => {
    const totals: TableRow[] = [];
    const breakdown: TableRow[] = [];

    sanitizeLineupRows.forEach(row => {
      const totalsData: TableRow = { lineup: row.lineup };
      if (row.statsTotals && typeof row.statsTotals === 'object') {
        Object.entries(row.statsTotals).forEach(([key, value]) => {
          if (!['LINEUP', 'RNK', 'TEAM', 'SEASON'].includes(key)) {
            totalsData[key] = value;
          }
        });
      }
      totals.push(totalsData);

      const breakdownData: TableRow = { lineup: row.lineup };
      if (row.breakdown && typeof row.breakdown === 'object') {
        Object.entries(row.breakdown).forEach(([key, value]) => {
          if (key !== 'LINEUP') {
            breakdownData[key] = value;
          }
        });
      }
      breakdown.push(breakdownData);
    });

    return { totalsRows: totals, breakdownRows: breakdown };
  }, [sanitizeLineupRows]);

  const deriveColumns = useMemo(() => {
    const buildColumns = (rows: TableRow[]): TableColumn[] => {
      const seen = new Set<string>();
      rows.forEach(row => {
        Object.keys(row).forEach(key => {
          if (key === 'lineup') return;
          if (!seen.has(key)) {
            seen.add(key);
          }
        });
      });

      const columnKeys = Array.from(seen);
      return [
        { key: 'lineup', label: 'LINEUP', isFixed: true },
        ...columnKeys.map(key => ({
          key,
          label: formatColumnLabel(key)
        }))
      ];
    };

    return {
      totalsColumns: buildColumns(totalsRows),
      breakdownColumns: buildColumns(breakdownRows)
    };
  }, [totalsRows, breakdownRows]);

  const hasTotalsData =
    totalsRows.some(row => Object.keys(row).length > 1) &&
    deriveColumns.totalsColumns.length > 1;

  const hasBreakdownData =
    breakdownRows.some(row => Object.keys(row).length > 1) &&
    deriveColumns.breakdownColumns.length > 1;

  const currentView =
    activeView === 'stats_totals'
      ? hasTotalsData
        ? 'stats_totals'
        : hasBreakdownData
          ? 'breakdown'
          : 'stats_totals'
      : hasBreakdownData
        ? 'breakdown'
        : hasTotalsData
          ? 'stats_totals'
          : 'breakdown';

  const columns =
    currentView === 'stats_totals'
      ? deriveColumns.totalsColumns
      : deriveColumns.breakdownColumns;

  const data =
    currentView === 'stats_totals'
      ? totalsRows
      : breakdownRows;

  const displayedData = data;

  if (loading) {
    return <LoadingAnimation text="Loading lineup table" dotInterval={1000} />;
  }

  if (error) {
    return (
      <StatsTable
        columns={[{ key: 'error', label: 'Error' }]}
        data={[{ error: error }]}
        noDataMessage={error}
      />
    );
  }

  if (!lineupStatsData || lineupStatsData.length === 0) {
    return (
      <StatsTable
        columns={[{ key: 'noData', label: 'No Data' }]}
        data={[{ noData: 'No lineup stats available' }]}
        noDataMessage="No lineup stats available"
      />
    );
  }

  return (
    <TableSection>
      <ToggleRow>
        <ToggleControl>
          <ToggleOption
            type="button"
            onClick={() => setActiveView('stats_totals')}
            $active={currentView === 'stats_totals'}
            disabled={!hasTotalsData}
          >
            Stats & Totals
          </ToggleOption>
          <ToggleOption
            type="button"
            onClick={() => setActiveView('breakdown')}
            $active={currentView === 'breakdown'}
            disabled={!hasBreakdownData}
          >
            Breakdown
          </ToggleOption>
        </ToggleControl>
      </ToggleRow>

      <ScrollWrapper>
        <ScrollInner>
          <div className="stats-table-scroll-content">
            <StatsTable
              columns={columns}
              data={loading ? [] : displayedData}
              loading={loading}
              loadingMessage="Loading lineup stats..."
              isReady={!loading}
              stickyFirstColumn
              stickyLeadingColumnCount={1}
              noDataMessage="No lineup stats available"
              disableContainerScroll
            />
          </div>
        </ScrollInner>
      </ScrollWrapper>
    </TableSection>
  );
};
